import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material";
import * as R from "ramda";
import { Sparklines, SparklinesLine } from "react-sparklines";

import { CHIP_COLORS, getScoreChipColor } from "../../../common/colors";

interface Call {
  interaction_id: string;
  user_email: string;
  created: string;
  attributes: {
    [key: string]: number | undefined;
  };
  call_summary: string;
  overall_score?: number;
  trend?: number[];
}

interface PerformanceTableProps {
  title?: string;
  subtitle?: string;
  data: {
    calls: Call[];
    trends: {
      [key: string]: number[][];
    };
  };
  onCallClick: (interactionId: string, attribute?: string) => void;
}

const PerformanceTable = ({ title, subtitle, data, onCallClick }: PerformanceTableProps) => {
  if (!data || !data.calls || data.calls.length === 0) {
    return (
      <Paper sx={{ p: 2 }}>
        <Typography variant="body2" align="center" sx={{ py: 2 }}>
          No data available
        </Typography>
      </Paper>
    );
  }

  // Get all attribute names from all calls
  const getAllAttributeNames = R.pipe(
    R.map((call: Call) => R.keys(call.attributes || {})),
    R.flatten,
    R.uniq
  );

  const attributeNames = getAllAttributeNames(data.calls) as string[];

  return (
    <>
      {title && (
        <>
          <Typography variant="h6" sx={{ mb: 0 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="subtitle1" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Attribute</TableCell>
              <TableCell>Trend</TableCell>
              {data.calls.map((call, index) => (
                <TableCell key={index} align="center" sx={{ minWidth: 50 }}>
                  <Tooltip
                    title={call.call_summary}
                    arrow
                    placement="top"
                    slotProps={{
                      popper: {
                        modifiers: [
                          {
                            name: "offset",
                            options: {
                              offset: [0, -8]
                            }
                          }
                        ]
                      }
                    }}>
                    <span>Call {index + 1}</span>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Overall Score Row */}
            <TableRow sx={{
              backgroundColor: "#f5f5f5"
            }}>
              <TableCell component="th" scope="row" sx={{ width: 300, fontWeight: 600 }}>
                Overall Score
              </TableCell>
              <TableCell sx={{ width: 100 }}>
                {data.trends["Overall Score"] && data.trends["Overall Score"].flat().length > 0 && (
                  <Sparklines data={data.trends["Overall Score"].flat()} width={100} height={30}>
                    <SparklinesLine color="#1976d2" />
                  </Sparklines>
                )}
              </TableCell>
              {data.calls.map((call) => {
                const overallScore = call.overall_score;
                const chipColor = typeof overallScore === "number" ? getScoreChipColor(overallScore) : CHIP_COLORS.yellow;

                return (
                  <TableCell
                    key={`${call.interaction_id}-overall`}
                    align="center"
                    sx={{
                      backgroundColor: chipColor.background,
                      color: chipColor.fontColor,
                      fontWeight: 600,
                      cursor: "pointer",
                      "&:hover": {
                        opacity: 0.9
                      },
                      width: 50
                    }}
                    onClick={() => onCallClick(call.interaction_id)}
                  >
                    {typeof overallScore === "number" ? overallScore.toFixed(2) : "N/A"}
                  </TableCell>
                );
              })}
            </TableRow>

            {/* Attribute Rows */}
            {attributeNames.map((attribute) => {
              // Get trends for this attribute
              const trendData = data.trends[attribute] || [];
              const flatTrend = trendData.flat();

              return (
                <TableRow key={attribute}>
                  <TableCell component="th" scope="row" sx={{ width: 300 }}>
                    {attribute}
                  </TableCell>
                  <TableCell sx={{ width: 100 }}>
                    {flatTrend.length > 0 && (
                      <Sparklines data={flatTrend} width={100} height={30}>
                        <SparklinesLine color="#1976d2" />
                      </Sparklines>
                    )}
                  </TableCell>
                  {data.calls.map((call) => {
                    const score = call.attributes[attribute] || "N/A";
                    const chipColor = typeof score === "number" ? getScoreChipColor(score) : CHIP_COLORS.grey;

                    return (
                      <TableCell
                        key={`${call.interaction_id}-${attribute}`}
                        align="center"
                        sx={{
                          backgroundColor: chipColor.background,
                          color: chipColor.fontColor,
                          cursor: "pointer",
                          "&:hover": {
                            opacity: 0.9
                          },
                          width: 50
                        }}
                        onClick={() => onCallClick(call.interaction_id, attribute)}
                      >
                        {typeof score === "number" ? score.toFixed(1) : score}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default PerformanceTable;
